import { Box, Button, Divider } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useEffect } from "react";

import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatItalicIcon from "@mui/icons-material/FormatItalic";
import FormatAlignLeftIcon from "@mui/icons-material/FormatAlignLeft";
import FormatAlignCenterIcon from "@mui/icons-material/FormatAlignCenter";
import FormatAlignRightIcon from "@mui/icons-material/FormatAlignRight";
import FormatAlignJustifyIcon from "@mui/icons-material/FormatAlignJustify";
import FormatUnderlinedIcon from '@mui/icons-material/FormatUnderlined';
import StrikethroughSIcon from '@mui/icons-material/StrikethroughS';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import HorizontalRuleIcon from '@mui/icons-material/HorizontalRule';
import UndoIcon from '@mui/icons-material/Undo';
import RedoIcon from '@mui/icons-material/Redo';
import FormatClearIcon from '@mui/icons-material/FormatClear';

import { EditorContent, useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from '@tiptap/extension-underline';
import TextAlign from "@tiptap/extension-text-align";

export default function TemplateEditor({ value, onChange }) {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  const editor = useEditor({
    extensions: [
      StarterKit,
      TextAlign.configure({ types: ["heading", "paragraph"] }),
      Underline, 
    ],
    content: value,
    immediatelyRender: false,
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
  });

  // Efecto para sincronizar si el valor cambia desde afuera (ej: al restaurar plantilla)
  useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value || "", false);
    }
  }, [value, editor]);

  if (!editor) return null;

  return (
    <Box>
      <Box sx={{ display: "flex", gap: 0.5, flexWrap: "wrap", mb: 1.2, bgcolor: isDark ? "rgba(255,255,255,0.05)" : "rgba(255,255,255,0.7)", p: 0.8, borderRadius: 2, border: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid rgba(0,0,0,0.08)" }}>
        
        <Button size="small" variant="text" onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()} sx={{ minWidth: 0, p: 0.5, color: "text.secondary" }}>
          <UndoIcon fontSize="small" />
        </Button>
        <Button size="small" variant="text" onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()} sx={{ minWidth: 0, p: 0.5, color: "text.secondary" }}>
          <RedoIcon fontSize="small" />
        </Button>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

        <Button size="small" variant={editor.isActive("bold") ? "contained" : "text"} onClick={() => editor.chain().focus().toggleBold().run()} sx={{ minWidth: 0, p: 0.5, color: editor.isActive("bold") ? '#fff' : 'text.primary' }}>
          <FormatBoldIcon fontSize="small" />
        </Button>
        <Button size="small" variant={editor.isActive("italic") ? "contained" : "text"} onClick={() => editor.chain().focus().toggleItalic().run()} sx={{ minWidth: 0, p: 0.5, color: editor.isActive("italic") ? '#fff' : 'text.primary' }}>
          <FormatItalicIcon fontSize="small" />
        </Button>
        <Button size="small" variant={editor.isActive("underline") ? "contained" : "text"} onClick={() => editor.chain().focus().toggleUnderline().run()} sx={{ minWidth: 0, p: 0.5, color: editor.isActive("underline") ? '#fff' : 'text.primary' }}>
          <FormatUnderlinedIcon fontSize="small" />
        </Button>
        <Button size="small" variant={editor.isActive("strike") ? "contained" : "text"} onClick={() => editor.chain().focus().toggleStrike().run()} sx={{ minWidth: 0, p: 0.5, color: editor.isActive("strike") ? '#fff' : 'text.primary' }}>
          <StrikethroughSIcon fontSize="small" />
        </Button>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

        <Button size="small" variant={editor.isActive("heading", { level: 1 }) ? "contained" : "text"} onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} sx={{ minWidth: 0, p: 0.5, fontWeight: "bold", color: editor.isActive("heading", { level: 1 }) ? '#fff' : 'text.primary' }}>
          H1
        </Button>
        <Button size="small" variant={editor.isActive("heading", { level: 2 }) ? "contained" : "text"} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} sx={{ minWidth: 0, p: 0.5, fontWeight: "bold", color: editor.isActive("heading", { level: 2 }) ? '#fff' : 'text.primary' }}>
          H2
        </Button>
        <Button size="small" variant={editor.isActive("paragraph") ? "contained" : "text"} onClick={() => editor.chain().focus().setParagraph().run()} sx={{ minWidth: 0, p: 0.5, fontWeight: "bold", color: editor.isActive("paragraph") ? '#fff' : 'text.primary' }}>
          P
        </Button>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

        <Button size="small" variant={editor.isActive("bulletList") ? "contained" : "text"} onClick={() => editor.chain().focus().toggleBulletList().run()} sx={{ minWidth: 0, p: 0.5, color: editor.isActive("bulletList") ? '#fff' : 'text.primary' }}>
          <FormatListBulletedIcon fontSize="small" />
        </Button>
        <Button size="small" variant={editor.isActive("orderedList") ? "contained" : "text"} onClick={() => editor.chain().focus().toggleOrderedList().run()} sx={{ minWidth: 0, p: 0.5, color: editor.isActive("orderedList") ? '#fff' : 'text.primary' }}>
          <FormatListNumberedIcon fontSize="small" />
        </Button>
        <Button size="small" variant={editor.isActive("blockquote") ? "contained" : "text"} onClick={() => editor.chain().focus().toggleBlockquote().run()} sx={{ minWidth: 0, p: 0.5, color: editor.isActive("blockquote") ? '#fff' : 'text.primary' }}>
          <FormatQuoteIcon fontSize="small" />
        </Button>
        <Button size="small" variant="text" onClick={() => editor.chain().focus().setHorizontalRule().run()} sx={{ minWidth: 0, p: 0.5, color: 'text.primary' }}>
          <HorizontalRuleIcon fontSize="small" />
        </Button>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

        <Button size="small" variant={editor.isActive({ textAlign: "left" }) ? "contained" : "text"} onClick={() => editor.chain().focus().setTextAlign("left").run()} sx={{ minWidth: 0, p: 0.5, color: editor.isActive({ textAlign: "left" }) ? '#fff' : 'text.primary' }}>
          <FormatAlignLeftIcon fontSize="small" />
        </Button>
        <Button size="small" variant={editor.isActive({ textAlign: "center" }) ? "contained" : "text"} onClick={() => editor.chain().focus().setTextAlign("center").run()} sx={{ minWidth: 0, p: 0.5, color: editor.isActive({ textAlign: "center" }) ? '#fff' : 'text.primary' }}>
          <FormatAlignCenterIcon fontSize="small" />
        </Button>
        <Button size="small" variant={editor.isActive({ textAlign: "right" }) ? "contained" : "text"} onClick={() => editor.chain().focus().setTextAlign("right").run()} sx={{ minWidth: 0, p: 0.5, color: editor.isActive({ textAlign: "right" }) ? '#fff' : 'text.primary' }}>
          <FormatAlignRightIcon fontSize="small" />
        </Button>
        <Button size="small" variant={editor.isActive({ textAlign: "justify" }) ? "contained" : "text"} onClick={() => editor.chain().focus().setTextAlign("justify").run()} sx={{ minWidth: 0, p: 0.5, color: editor.isActive({ textAlign: "justify" }) ? '#fff' : 'text.primary' }}>
          <FormatAlignJustifyIcon fontSize="small" />
        </Button>

        <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

        <Button size="small" variant="text" onClick={() => editor.chain().focus().unsetAllMarks().clearNodes().run()} sx={{ minWidth: 0, p: 0.5, color: "error.main" }} title="Limpiar formato">
          <FormatClearIcon fontSize="small" />
        </Button>

      </Box>

      <Box
        sx={{
          border: isDark ? "1px solid rgba(255,255,255,0.08)" : "1px solid rgba(0,0,0,0.08)",
          borderRadius: 3,
          background: isDark ? "rgba(255,255,255,0.04)" : "rgba(255,255,255,0.9)",
          p: 2,
          minHeight: 250,
          "& .ProseMirror": {
            outline: "none",
            minHeight: 200,
            lineHeight: 1.6,
            color: isDark ? "rgba(255,255,255,0.87)" : "rgba(0,0,0,0.87)",
            "& p": { margin: "0.5em 0" },
            "& blockquote": {
              borderLeft: `3px solid ${theme.palette.primary.main}`,
              margin: "1em 0",
              paddingLeft: "1em",
              opacity: 0.8,
              fontStyle: "italic",
            },
            "& hr": { border: "none", borderTop: isDark ? "1px solid rgba(255,255,255,0.14)" : "1px solid rgba(0,0,0,0.2)", margin: "1em 0" },
          },
        }}
      >
        <EditorContent editor={editor} />
      </Box>
    </Box>
  );
}